/** @file Per-vertex inverse Jacobians: a world drag delta → the delta of a mesh's rest or deform vertices. */
import { mountMatrix } from '@pose2d/compiler/compile/mountMatrix';
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { Pose, SkeletonData } from '@pose2d/core/types';
import type { Doc, MeshAtt } from '@pose2d/format/types';
import { slotWorld } from '@pose2d/geometry/slotWorld';
import type { Vec2 } from '@pose2d/math/types';
import type { CompiledTarget } from './compiledTarget';
import type { MeshTarget } from './meshTarget';

type Lin = [number, number, number, number];
type Skinned = Extract<MeshAtt['geometry'], { kind: 'skinned' }>;

const WS = POSE_LAYOUT.WORLD_STRIDE;
const STRIDE = 4;

/** Linear part [a, b, c, d] of the affine block at `o` (x' = a·x + b·y + tx, y' = c·x + d·y + ty). */
const linOf = (m: ArrayLike<number>, o: number): Lin => [m[o] ?? 0, m[o + 1] ?? 0, m[o + 2] ?? 0, m[o + 3] ?? 0];
/** p·q for 2×2 matrices stored [a, b, c, d]. */
const mul = (p: Lin, q: Lin): Lin => [p[0] * q[0] + p[1] * q[2], p[0] * q[1] + p[1] * q[3], p[2] * q[0] + p[3] * q[2], p[2] * q[1] + p[3] * q[3]];

/** Inverse of m, or all zeros when |det| ≤ 1e-12 (such a vertex does not follow a drag). */
function inverse(m: Lin): Lin {
  const det = m[0] * m[3] - m[1] * m[2];
  return Math.abs(det) > 1e-12 ? [m[3] / det, -m[1] / det, -m[2] / det, m[0] / det] : [0, 0, 0, 0];
}

/** Rigid: Lin(slot world) · Lin(document mount) — the compiler bakes the mount into the vertices (meshRecord). */
function rigidJacobian(doc: Doc, data: SkeletonData, pose: Pose, target: MeshTarget, at: CompiledTarget): Lin {
  const world = new Float64Array(WS);
  slotWorld(pose, data, at.slot, world);
  const image = doc.images[target.mesh.image];
  const mount = image === undefined ? null : mountMatrix(doc, target.attachment, target.mesh, image);
  return mount === null ? linOf(world, 0) : mul(linOf(world, 0), linOf(mount, 0));
}

/** Skinned vertex v: Σ wᵢ · Lin(Wᵢ) · Lin(Bᵢ)⁻¹ over its document influences (posed worlds by bone name). */
function skinnedJacobian(doc: Doc, data: SkeletonData, pose: Pose, g: Skinned, v: number): Lin {
  const j: Lin = [0, 0, 0, 0];
  for (const [bone, w] of g.weights[v] ?? []) {
    const name = doc.bones[bone]?.name;
    const b = name === undefined ? undefined : data.boneIndex.get(name);
    const bind = g.bindPose[bone];
    if (b === undefined || bind === undefined) continue;
    const k = mul(linOf(pose.world, b * WS), inverse(linOf(bind, 0)));
    for (let i = 0; i < STRIDE; i++) j[i] = (j[i] ?? 0) + w * (k[i] ?? 0);
  }
  return j;
}

/** Inverse Jacobians of every vertex of a mesh target in the posed scene (the setup pose in Setup mode). */
export const vertexFrames = {
  /** Numbers per vertex: the inverse 2×2 [a, b, c, d]. */
  STRIDE,
  /** One inverse Jacobian per vertex: rest (or deform) delta = J⁻¹ · world delta, exact for rigid and skinned meshes. */
  of(doc: Doc, data: SkeletonData, pose: Pose, target: MeshTarget, at: CompiledTarget): Float64Array {
    const g = target.mesh.geometry;
    const count = target.mesh.uvs.length / 2;
    const out = new Float64Array(count * STRIDE);
    if (g.kind === 'rigid') {
      const frame = inverse(rigidJacobian(doc, data, pose, target, at));
      for (let v = 0; v < count; v++) out.set(frame, v * STRIDE);
      return out;
    }
    for (let v = 0; v < count; v++) out.set(inverse(skinnedJacobian(doc, data, pose, g, v)), v * STRIDE);
    return out;
  },
  /** Writes J⁻¹ᵥ · (dx, dy) into out and returns it. */
  apply(frames: Float64Array, v: number, dx: number, dy: number, out: Vec2): Vec2 {
    const o = v * STRIDE;
    out[0] = (frames[o] ?? 0) * dx + (frames[o + 1] ?? 0) * dy;
    out[1] = (frames[o + 2] ?? 0) * dx + (frames[o + 3] ?? 0) * dy;
    return out;
  },
};
